import { CssBaseline } from "@mui/material";
import React from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import AppLayout from "./components/layout/AppLayout";
import ProtectedRoute from "./components/layout/ProtectedRoute";
import PublicRoute from "./components/layout/PublicRoute";
import SubscriptionLevelGate from "./components/layout/SubscriptionLevelGate";
import { SignalRProvider } from "./contexts/SignalRContext";
import AuditPage from "./features/audit/AuditPage";
import ForgotPasswordPage from "./features/auth/ForgotPasswordPage";
import LoginPage from "./features/auth/LoginPage";
import SignupPage from "./features/auth/SignupPage";
import PasswordSetupPage from "./features/auth/PasswordSetupPage";
import ResetPasswordPage from "./features/auth/ResetPasswordPage";
import DashboardPage from "./features/dashboard/DashboardPage";
import SectionHubPage from "./features/hubs/SectionHubPage";
import DocumentsPage from "./features/documents/DocumentsPage";
import EditProfilePage from "./features/profile/EditProfilePage";
import ShopsPage from "./features/shops/ShopsPage";
import TrailerCreatePage from "./features/trailers/TrailerCreatePage";
import TrailerEditPage from "./features/trailers/TrailerEditPage";
import TrailersPage from "./features/trailers/TrailersPage";
// import TruckAssignmentsPage from "./features/truck-assignments/TruckAssignmentsPage";
// import TrailerAssignmentsPage from "./features/trailer-assignments/TrailerAssignmentsPage";
import DriverAssignmentsPage from "./features/driver-assignments/DriverAssignmentsPage";
import TruckDriverPage from "./features/truck-driver/TruckDriverPage";
import UserCreatePage from "./features/users/UserCreatePage";
import UserEditPage from "./features/users/UserEditPage";
import UsersPage from "./features/users/UsersPage";
import VehicleCreatePage from "./features/vehicles/VehicleCreatePage";
import VehicleEditPage from "./features/vehicles/VehicleEditPage";
import VehiclesPage from "./features/vehicles/VehiclesPage";
import ClaimsPage from "./features/claims/ClaimsPage";
import ClaimCreatePage from "./features/claims/ClaimCreatePage";
import ClaimEditPage from "./features/claims/ClaimEditPage";
import DotInspectionsPage from "./features/dotInspections/DotInspectionsPage";
import DotInspectionCreatePage from "./features/dotInspections/DotInspectionCreatePage";
import DotInspectionEditPage from "./features/dotInspections/DotInspectionEditPage";
import SafetyPage from "./features/safety/SafetyPage";
import VehiclePermitsPage from "./features/permits/VehiclePermitsPage";
import AdminAbsencesPage from "./features/absence/AdminAbsencesPage";
import TenantsPage from "./features/tenants/TenantsPage";
import TenantCreatePage from "./features/tenants/TenantCreatePage";
import TenantEditPage from "./features/tenants/TenantEditPage";
import IntegrationAccountsPage from "./features/integrations/IntegrationAccountsPage";
import IftaReportPage from "./features/reports/IftaReportPage";
import SubscriptionDashboard from "./features/subscription/SubscriptionDashboard";
import SubscriptionRequiredPage from "./features/subscription/SubscriptionRequiredPage";
import SubscriptionPage from "./features/subscription/SubscriptionPage";
import NotFoundPage from "./features/not-found/NotFoundPage";
import { CustomThemeProvider } from "./theme/ThemeContext";
import { UserRoles } from "./constants/roles";
import PublicTrackingPage from "./features/public-tracking/PublicTrackingPage";
import ShareLinksPage from "./features/share-links/ShareLinksPage";
import PtiPage from "./features/pti/PtiPage";
import PtiReviewPage from "./features/pti/PtiReviewPage";
import MaintenancePage from "./features/maintenance/MaintenancePage";
import DetentionLumpersPage from "./features/detention-lumpers/DetentionLumpersPage";
import { ToastProvider } from "./contexts/ToastContext";
import LoggingOffOverlay from "./features/auth/LoggingOffOverlay";

const NON_DRIVER_ROLES = [
  UserRoles.ADMIN,
  UserRoles.DISPATCHER,
  UserRoles.SUPER_ADMIN,
  UserRoles.FLEET,
  UserRoles.SAFETY,
  UserRoles.IFTA_ADMIN,
  UserRoles.RECRUITING,
  UserRoles.ACCOUNTING,
];

const PTI_MANAGEMENT_ROLES = [
  UserRoles.ADMIN,
  UserRoles.FLEET,
  UserRoles.SUPER_ADMIN,
];

const App: React.FC = () => {
  return (
    <CustomThemeProvider>
      <ToastProvider>
        <SignalRProvider>
          <CssBaseline />
          <LoggingOffOverlay />
          <Router>
            <Routes>
              {/* Public routes - Protected from authenticated users */}
              <Route element={<PublicRoute />}>
                <Route path="/" element={<SignupPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
              </Route>
              <Route path="/setup-password" element={<PasswordSetupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route
                path="/subscription-required"
                element={<SubscriptionRequiredPage />}
              />
              <Route
                path="/permits/:vehicleId"
                element={<VehiclePermitsPage />}
              />
              <Route path="/track/:token" element={<PublicTrackingPage />} />

              {/* SuperAdmin-only routes */}
              <Route element={<ProtectedRoute requiredRole="SuperAdmin" />}>
                <Route
                  path="/tenants"
                  element={
                    <AppLayout>
                      <TenantsPage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/tenants/create"
                  element={
                    <AppLayout>
                      <TenantCreatePage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/tenants/:id/edit"
                  element={
                    <AppLayout>
                      <TenantEditPage />
                    </AppLayout>
                  }
                />
                <Route path="/" element={<Navigate replace to="/tenants" />} />
              </Route>

              <Route
                element={
                  <ProtectedRoute
                    allowedRoles={[UserRoles.ADMIN, UserRoles.SUPER_ADMIN]}
                  />
                }
              >
                <Route
                  path="/audit"
                  element={
                    <AppLayout>
                      <AuditPage />
                    </AppLayout>
                  }
                />
              </Route>

              <Route element={<ProtectedRoute />}>
                <Route
                  path="/dashboard"
                  element={
                    <AppLayout>
                      <DashboardPage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/hubs/:section"
                  element={
                    <AppLayout>
                      <SectionHubPage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <AppLayout>
                      <EditProfilePage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/subscription"
                  element={
                    <AppLayout>
                      <SubscriptionDashboard />
                    </AppLayout>
                  }
                />
                <Route
                  path="/subscriptions"
                  element={
                    <AppLayout>
                      <SubscriptionPage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/shops"
                  element={
                    <AppLayout>
                      <ShopsPage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/vehicles"
                  element={
                    <AppLayout>
                      <VehiclesPage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/vehicles/create"
                  element={
                    <AppLayout>
                      <VehicleCreatePage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/vehicles/:id/edit"
                  element={
                    <AppLayout>
                      <VehicleEditPage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/trailers"
                  element={
                    <AppLayout>
                      <TrailersPage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/trailers/create"
                  element={
                    <AppLayout>
                      <TrailerCreatePage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/trailers/:id/edit"
                  element={
                    <AppLayout>
                      <TrailerEditPage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/map"
                  element={
                    <AppLayout>
                      <div>Map Page (Under Construction)</div>
                    </AppLayout>
                  }
                />
                <Route
                  path="/drivers"
                  element={
                    <AppLayout>
                      <div>Drivers Page (Under Construction)</div>
                    </AppLayout>
                  }
                />
                <Route
                  element={<SubscriptionLevelGate requiredLevel="Pro" />}
                >
                  <Route
                    path="/reports/ifta"
                    element={
                      <AppLayout>
                        <IftaReportPage />
                      </AppLayout>
                    }
                  />
                </Route>
                <Route
                  path="/"
                  element={<Navigate replace to="/dashboard" />}
                />
              </Route>
              {/* Documents and Users — accessible by all non-driver roles */}
              <Route
                element={<ProtectedRoute allowedRoles={NON_DRIVER_ROLES} />}
              >
                <Route
                  path="/documents"
                  element={
                    <AppLayout>
                      <DocumentsPage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <AppLayout>
                      <UsersPage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/users/create"
                  element={
                    <AppLayout>
                      <UserCreatePage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/users/:id/edit"
                  element={
                    <AppLayout>
                      <UserEditPage />
                    </AppLayout>
                  }
                />
              </Route>
              {/* Admin-only routes */}
              <Route element={<ProtectedRoute requiredRole="Admin" />}>
                {/* Standard subscription required */}
                <Route
                  element={<SubscriptionLevelGate requiredLevel="Standard" />}
                >
                  {/* <Route
                  path="/admin/truck-assignments"
                  element={
                    <AppLayout>
                      <TruckAssignmentsPage />
                    </AppLayout>
                  }
                /> */}
                  {/* <Route
                  path="/admin/trailer-assignments"
                  element={
                    <AppLayout>
                      <TrailerAssignmentsPage />
                    </AppLayout>
                  }
                /> */}
                  <Route
                    path="/admin/absences"
                    element={
                      <AppLayout>
                        <AdminAbsencesPage />
                      </AppLayout>
                    }
                  />
                </Route>
                <Route
                  path="/admin/integrations"
                  element={
                    <AppLayout>
                      <IntegrationAccountsPage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/admin/share-links"
                  element={
                    <AppLayout>
                      <ShareLinksPage />
                    </AppLayout>
                  }
                />
              </Route>

              {/* Driver Assignments — all roles except TruckDriver */}
              <Route
                element={<ProtectedRoute allowedRoles={NON_DRIVER_ROLES} />}
              >
                <Route
                  element={<SubscriptionLevelGate requiredLevel="Standard" />}
                >
                  <Route
                    path="/admin/driver-assignments"
                    element={
                      <AppLayout>
                        <DriverAssignmentsPage />
                      </AppLayout>
                    }
                  />
                </Route>
              </Route>

              {/* Maintenance — Admin and Fleet only */}
              <Route
                element={
                  <ProtectedRoute
                    allowedRoles={[UserRoles.ADMIN, UserRoles.FLEET]}
                  />
                }
              >
                <Route
                  path="/maintenance"
                  element={
                    <AppLayout>
                      <MaintenancePage />
                    </AppLayout>
                  }
                />
                <Route
                  path="/maintenance/trailers"
                  element={
                    <AppLayout>
                      <MaintenancePage />
                    </AppLayout>
                  }
                />
              </Route>

              {/* Detention & Lumper — Admin, Dispatcher, Accounting */}
              <Route
                element={
                  <ProtectedRoute
                    allowedRoles={[
                      UserRoles.ADMIN,
                      UserRoles.DISPATCHER,
                      UserRoles.ACCOUNTING,
                    ]}
                  />
                }
              >
                <Route
                  path="/detention-lumpers"
                  element={
                    <AppLayout>
                      <DetentionLumpersPage />
                    </AppLayout>
                  }
                />
              </Route>

              {/* PTI management — Admin, Fleet, SuperAdmin */}
              <Route
                element={<ProtectedRoute allowedRoles={PTI_MANAGEMENT_ROLES} />}
              >
                <Route
                  element={<SubscriptionLevelGate requiredLevel="Standard" />}
                >
                  <Route
                    path="/pti"
                    element={
                      <AppLayout>
                        <PtiPage />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/pti/:id"
                    element={
                      <AppLayout>
                        <PtiReviewPage />
                      </AppLayout>
                    }
                  />
                </Route>
              </Route>

              {/* Claims Board & Safety Board — Admin and Safety only */}
              <Route
                element={
                  <ProtectedRoute
                    allowedRoles={[
                      UserRoles.ADMIN,
                      UserRoles.SAFETY,
                      UserRoles.IFTA_ADMIN,
                    ]}
                  />
                }
              >
                <Route
                  element={
                    <ProtectedRoute
                      allowedRoles={[
                        UserRoles.ADMIN,
                        UserRoles.SAFETY,
                        UserRoles.IFTA_ADMIN,
                      ]}
                    />
                  }
                >
                  <Route
                    path="/claims"
                    element={
                      <AppLayout>
                        <ClaimsPage />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/claims/create"
                    element={
                      <AppLayout>
                        <ClaimCreatePage />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/claims/:id/edit"
                    element={
                      <AppLayout>
                        <ClaimEditPage />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/dot-inspections"
                    element={
                      <AppLayout>
                        <DotInspectionsPage />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/dot-inspections/create"
                    element={
                      <AppLayout>
                        <DotInspectionCreatePage />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/dot-inspections/:id/edit"
                    element={
                      <AppLayout>
                        <DotInspectionEditPage />
                      </AppLayout>
                    }
                  />
                  <Route
                    path="/safety"
                    element={
                      <AppLayout>
                        <SafetyPage />
                      </AppLayout>
                    }
                  />
                </Route>
              </Route>

              {/* Truck Driver Routes (Standard subscription required) */}
              <Route element={<ProtectedRoute requiredRole="TruckDriver" />}>
                <Route
                  element={<SubscriptionLevelGate requiredLevel="Standard" />}
                >
                  <Route
                    path="/truck-driver"
                    element={
                      <AppLayout>
                        <TruckDriverPage />
                      </AppLayout>
                    }
                  />
                </Route>
              </Route>

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Router>
        </SignalRProvider>
      </ToastProvider>
    </CustomThemeProvider>
  );
};

export default App;
